// The question model.
//
// A questionnaire template is DATA, not code: it is authored once per financial
// year, stored in the database, and shipped to three different clients (API,
// web, mobile). That means it crosses a trust boundary every time it is loaded,
// so every shape here has a Zod schema and the TypeScript types are derived
// from those schemas rather than declared separately. One definition, validated
// at the edge, typed everywhere.
//
// ---------------------------------------------------------------------------
// THE ONE RULE THAT MATTERS: question codes are permanent.
// ---------------------------------------------------------------------------
// Every question carries a `code` (e.g. 'D1_COST_FUEL'). Answers, ITR mappings
// and prior-year prefill are all keyed to the code, never to a database row id.
//
// Reuse a code next year and that question inherits last year's answer as
// prefill and keeps its ITR mapping, for free. Change a code and it becomes a
// brand new question with no history. So renaming a code is a breaking change
// to client data: treat it like a database migration, not an edit.
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Codes
// ---------------------------------------------------------------------------
// Deliberately restrictive: uppercase, digits and underscores only. Codes end
// up in spreadsheet columns, URLs and OneDrive paths, so anything that would
// need escaping somewhere is banned at the source.
export const questionCodeSchema = z
    .string()
    .min(2)
    .max(60)
    .regex(/^[A-Z][A-Z0-9_]*$/, 'Codes must be UPPER_SNAKE_CASE, e.g. D1_COST_FUEL');
export const sectionCodeSchema = questionCodeSchema;
// ---------------------------------------------------------------------------
// Question types
// ---------------------------------------------------------------------------
export const questionTypeSchema = z.enum([
    'heading', // not a question — explanatory text in the flow
    'text',
    'longtext',
    'number',
    'currency',
    'date',
    'yesno', // yes | no | unsure
    'radio', // one of, rendered as cards
    'select', // one of, rendered as a picker
    'multiselect', // many of
    'fileonly', // no value of its own, just an upload target
    'repeater', // a repeating block, e.g. one per rental property
]);
// Types that never hold a value. Progress and validation must skip these, or a
// client could never reach 100%.
export const VALUELESS_TYPES = new Set(['heading', 'fileonly']);
export const optionSchema = z.object({
    value: z.string().min(1),
    label: z.string().min(1),
});
// ---------------------------------------------------------------------------
// Conditional visibility
// ---------------------------------------------------------------------------
//
// Rules look like:
//   { code: 'HAS_RENTAL', op: 'eq', value: 'yes' }
//   { all: [ ... ] }   { any: [ ... ] }   { not: { ... } }
//
// 81 of the 189 FY2026 questions are conditional, which is what keeps the form
// at roughly 102 questions for a typical individual instead of all 189.
//
// Note what is NOT here: any 'unknown operator' escape hatch. The previous
// version of this engine returned `true` for an unrecognised operator, so a
// typo in a rule silently showed a question to every client and nobody found
// out. Here the operator set is closed, Zod rejects anything else when the
// template loads, and the evaluator is exhaustive. A bad rule now fails loudly
// at author time instead of quietly in front of a client.
export const comparisonOpSchema = z.enum([
    'eq',
    'ne',
    'in',
    'gt',
    'lt',
    'filled', // has any answer
    'empty', // has no answer
]);
const leafRuleBase = z.object({
    code: questionCodeSchema,
    op: comparisonOpSchema,
    value: z
        .union([z.string(), z.number(), z.boolean(), z.array(z.union([z.string(), z.number()]))])
        .optional(),
});
// `value` is required for the operators that compare, and forbidden for the two
// that don't. Encoding that here means the evaluator never has to guess.
export const leafRuleSchema = leafRuleBase.superRefine((rule, ctx) => {
    const needsValue = rule.op !== 'filled' && rule.op !== 'empty';
    if (needsValue && rule.value === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Operator '${rule.op}' needs a value`,
        });
    }
    if (!needsValue && rule.value !== undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Operator '${rule.op}' must not have a value`,
        });
    }
    if (rule.op === 'in' && !Array.isArray(rule.value)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Operator 'in' needs an array value",
        });
    }
});
export const visibilityRuleSchema = z.lazy(() => z.union([
    leafRuleSchema,
    z.object({ all: z.array(visibilityRuleSchema).min(1) }),
    z.object({ any: z.array(visibilityRuleSchema).min(1) }),
    z.object({ not: visibilityRuleSchema }),
]));
// ---------------------------------------------------------------------------
// Repeater sub-fields
// ---------------------------------------------------------------------------
//
// A repeater holds a list of rows: one rental property, one dividend, one
// dependant. Sub-fields are a deliberately flatter, simpler shape than a full
// question — no nesting, no conditionals, no uploads. This replaces the paper
// form's fixed "1. 2. 3. 4." rows, so a client with five children is no longer
// stuck.
export const repeaterFieldSchema = z.object({
    // Lower-case, because these are keys inside a row rather than top-level
    // answer codes. Keeping the two namespaces visually distinct makes it
    // obvious at a glance which one you are looking at.
    code: z
        .string()
        .min(1)
        .max(40)
        .regex(/^[a-z][a-z0-9_]*$/, 'Repeater field codes must be lower_snake_case'),
    type: z.enum(['text', 'longtext', 'number', 'currency', 'date', 'yesno', 'select']),
    label: z.string().min(1),
    options: z.array(optionSchema).optional(),
});
// ---------------------------------------------------------------------------
// Questions
// ---------------------------------------------------------------------------
export const questionSchema = z
    .object({
    code: questionCodeSchema,
    type: questionTypeSchema,
    label: z.string().min(1),
    /** Plain-English guidance shown under the label. */
    help: z.string().optional(),
    placeholder: z.string().optional(),
    /**
     * Blocks nothing — a client can always submit an incomplete form, because a
     * partly-filled questionnaire is still useful to the preparer. This only
     * drives the review screen and the export's "REQUIRED — blank" flag.
     */
    required: z.boolean().default(false),
    options: z.array(optionSchema).optional(),
    visibleIf: visibilityRuleSchema.optional(),
    repeaterOf: z.array(repeaterFieldSchema).optional(),
    allowFiles: z.boolean().default(false),
    filesHint: z.string().optional(),
    /**
     * Carry last year's answer forward as a suggested default.
     *
     * Off for anything that asks "did this happen THIS year". A client must
     * answer "did you sell any property" fresh every year; pre-ticking it from
     * last year would be actively wrong.
     */
    prefill: z.boolean().default(true),
})
    .superRefine((q, ctx) => {
    // Cross-field rules the plain shape cannot express. Split into named
    // checks because this is the most-read validation in the codebase and a
    // single 40-line closure is harder to extend safely.
    checkOptions(q, ctx);
    checkRepeater(q, ctx);
    checkValuelessConstraints(q, ctx);
});
/** Option-bearing types must have options; the rest must not. */
function checkOptions(q, ctx) {
    const needsOptions = q.type === 'radio' || q.type === 'select' || q.type === 'multiselect';
    const hasOptions = Boolean(q.options && q.options.length > 0);
    if (needsOptions && !hasOptions) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['options'],
            // Catching this at load time is the difference between "the template is
            // wrong" and "a client saw an empty dropdown".
            message: `'${q.type}' needs at least one option`,
        });
    }
    if (!needsOptions && hasOptions) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['options'],
            message: `'${q.type}' must not define options`,
        });
    }
}
function checkRepeater(q, ctx) {
    if (q.type === 'repeater' && (!q.repeaterOf || q.repeaterOf.length === 0)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['repeaterOf'],
            message: "'repeater' needs at least one sub-field",
        });
        return;
    }
    if (q.type !== 'repeater' && q.repeaterOf) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['repeaterOf'],
            message: "Only 'repeater' may define repeaterOf",
        });
        return;
    }
    if (!q.repeaterOf)
        return;
    // A duplicated sub-field code would silently overwrite a value in the row.
    const seen = new Set();
    for (const field of q.repeaterOf) {
        if (seen.has(field.code)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['repeaterOf'],
                message: `Duplicate repeater field code '${field.code}'`,
            });
        }
        seen.add(field.code);
    }
}
function checkValuelessConstraints(q, ctx) {
    // A required heading could never be satisfied, so progress would stick below
    // 100% forever with nothing the client could do about it.
    if (VALUELESS_TYPES.has(q.type) && q.required) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['required'],
            message: `'${q.type}' holds no value, so it cannot be required`,
        });
    }
    if (!q.allowFiles && q.filesHint) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['filesHint'],
            message: 'filesHint is meaningless without allowFiles',
        });
    }
}
// ---------------------------------------------------------------------------
// Sections and templates
// ---------------------------------------------------------------------------
export const sectionSchema = z.object({
    code: sectionCodeSchema,
    title: z.string().min(1),
    /** Plain-English intro at the top of the screen. */
    blurb: z.string().optional(),
    /** Emoji or short glyph for the mobile section nav. */
    icon: z.string().max(8).optional(),
    /** Hide the whole section, e.g. no rental questions for someone with no rental. */
    visibleIf: visibilityRuleSchema.optional(),
    questions: z.array(questionSchema).min(1),
});
export const entityTypeSchema = z.enum(['individual', 'company', 'trust', 'partnership', 'smsf']);
export const templateSchema = z
    .object({
    /** Financial year ENDING. 2026 means 1 Jul 2025 to 30 Jun 2026. */
    year: z.number().int().min(2000).max(2100),
    entityType: entityTypeSchema,
    title: z.string().min(1),
    sections: z.array(sectionSchema).min(1),
})
    .superRefine((t, ctx) => {
    const codes = checkDuplicateCodes(t.sections, ctx);
    checkRuleReferences(t.sections, codes, ctx);
});
/**
 * Reject duplicate codes and return the full set of question codes.
 *
 * Duplicates are the one error here that would corrupt client data rather than
 * merely look wrong: two questions sharing a code read and write the same
 * answer, so one silently shadows the other.
 */
function checkDuplicateCodes(sections, ctx) {
    const questionCodes = new Set();
    const sectionCodes = new Set();
    for (const section of sections) {
        if (sectionCodes.has(section.code)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Duplicate section code '${section.code}'`,
            });
        }
        sectionCodes.add(section.code);
        for (const q of section.questions) {
            if (questionCodes.has(q.code)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `Duplicate question code '${q.code}' (in section '${section.code}')`,
                });
            }
            questionCodes.add(q.code);
        }
    }
    return questionCodes;
}
/**
 * Every visibility rule must refer to a question that exists.
 *
 * A rule naming a code that is not in the template can never be satisfied, so
 * the question it guards is invisible to every client forever — which is
 * indistinguishable from the question being missing. Hence an error, not a
 * warning.
 */
function checkRuleReferences(sections, knownCodes, ctx) {
    const walk = (rule, where) => {
        if ('all' in rule) {
            for (const r of rule.all)
                walk(r, where);
            return;
        }
        if ('any' in rule) {
            for (const r of rule.any)
                walk(r, where);
            return;
        }
        if ('not' in rule) {
            walk(rule.not, where);
            return;
        }
        if (!knownCodes.has(rule.code)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `${where} refers to unknown question code '${rule.code}'`,
            });
        }
    };
    for (const section of sections) {
        if (section.visibleIf)
            walk(section.visibleIf, `Section '${section.code}'`);
        for (const q of section.questions) {
            if (q.visibleIf)
                walk(q.visibleIf, `Question '${q.code}'`);
        }
    }
}
// ---------------------------------------------------------------------------
// Answers
// ---------------------------------------------------------------------------
//
// One permissive value type covering every question type. Answers are stored
// encrypted as JSON, so a single column holds all of these shapes.
export const repeaterRowSchema = z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()]));
export const answerValueSchema = z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(z.string()), // multiselect
    z.array(repeaterRowSchema), // repeater
]);
// ---------------------------------------------------------------------------
// Parsing helpers
// ---------------------------------------------------------------------------
/**
 * Validate a template, throwing a readable error that lists every problem at
 * once rather than stopping at the first.
 *
 * Use this at every boundary where a template enters the system: seeding, the
 * template editor's save, and tests. A template that reaches a client
 * unvalidated is a template that can misbehave silently.
 */
export function parseTemplate(input) {
    const result = templateSchema.safeParse(input);
    if (!result.success) {
        const problems = result.error.issues
            .map((i) => `  - ${i.path.join('.') || '(root)'}: ${i.message}`)
            .join('\n');
        throw new Error(`Template is not valid:\n${problems}`);
    }
    return result.data;
}
/** Every question in a template, flattened, in document order. */
export function allQuestions(template) {
    return template.sections.flatMap((s) => s.questions);
}
/** Look up a question by code. */
export function questionByCode(template, code) {
    for (const section of template.sections) {
        const found = section.questions.find((q) => q.code === code);
        if (found)
            return found;
    }
    return undefined;
}
/** The section a question belongs to. */
export function sectionForQuestion(template, code) {
    return template.sections.find((s) => s.questions.some((q) => q.code === code));
}
//# sourceMappingURL=question.js.map